// Subdomain utilities for Mus.Link

const MAIN_DOMAIN = 'mus.link';
const APP_SUBDOMAIN = 'app';
const RESERVED_SUBDOMAINS = new Set(['www', 'api', APP_SUBDOMAIN, 'dev']);

export function isLocalHost(host = window.location.hostname.toLowerCase()) {
  return host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host);
}

/**
 * Extract subdomain from current hostname
 * @returns {string|null} subdomain or null if on main domain
 */
export function getSubdomain() {
  const host = window.location.hostname.toLowerCase();
  
  // Check for localhost or IP (development)
  if (isLocalHost(host)) {
    // Check for subdomain in URL params for testing
    const params = new URLSearchParams(window.location.search);
    return params.get('subdomain') || null;
  }
  
  // Check if host ends with main domain
  if (host.endsWith(`.${MAIN_DOMAIN}`)) {
    const subdomain = host.replace(`.${MAIN_DOMAIN}`, '');
    if (subdomain && !RESERVED_SUBDOMAINS.has(subdomain)) {
      return subdomain;
    }
  }
  
  return null;
}

/**
 * Check if we're on a subdomain
 * @returns {boolean}
 */
export function isSubdomain() {
  return getSubdomain() !== null;
}

/**
 * Check if the current host is the app/cabinet subdomain
 * @returns {boolean}
 */
export function isAppSubdomain() {
  return window.location.hostname.toLowerCase() === `${APP_SUBDOMAIN}.${MAIN_DOMAIN}`;
}

/**
 * Build URL for a subdomain
 * @param {string} subdomain 
 * @param {string} path 
 * @returns {string}
 */
export function buildSubdomainUrl(subdomain, path = '') {
  const protocol = window.location.protocol;
  return `${protocol}//${subdomain}.${MAIN_DOMAIN}${path}`;
}

/**
 * Get main domain URL
 * @param {string} path 
 * @returns {string}
 */
export function getMainDomainUrl(path = '') {
  const protocol = window.location.protocol;
  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host)) {
    return `${window.location.origin}${path}`;
  }
  return `${protocol}//${MAIN_DOMAIN}${path}`;
}

/**
 * Get app/cabinet URL
 * @param {string} path
 * @returns {string}
 */
export function getAppDomainUrl(path = '') {
  const protocol = window.location.protocol;
  const host = window.location.hostname.toLowerCase();
  if (isLocalHost(host)) {
    return `${window.location.origin}${path}`;
  }
  return `${protocol}//${APP_SUBDOMAIN}.${MAIN_DOMAIN}${path}`;
}

export default {
  getSubdomain,
  isSubdomain,
  isAppSubdomain,
  isLocalHost,
  buildSubdomainUrl,
  getMainDomainUrl,
  getAppDomainUrl,
  APP_SUBDOMAIN,
  RESERVED_SUBDOMAINS,
  MAIN_DOMAIN
};
